<template>
    <div
        v-if="iFrameSettings"
        :class="{'inject-theming': useCustomTheme, 'disable-container': useFixedMargin}"
        :style="useFixedMargin ? 'margin-right:'+ fixedMargin + 'px;margin-left:' + fixedMargin + 'px;' : ''"
        class="container pt-3 pb-6">
        <div v-if="showingSingleEvent">
            <IFrameSingleEvent
                :event="singleEventToShow"
                :i-frame-type="iFrameSettings.Type"
                :loading="loading"
                :hide-categories="hideCategories"
                closeable
                :back-button-label="displaySingleEventButtonLabel"
                @show-main-page="$emit('showMainPage')" />
            <JsonLdEvent
                v-if="embedStructuredData"
                :event="singleEventToShow"
                :parent-url="parentUrl" />
        </div>
        <div v-else-if="embedStructuredData">
            <JsonLdEvent
                v-for="event in eventsValidForIndexing"
                :key="'json-ld-' + event.id"
                :event="event"
                :parent-url="parentUrl" />
        </div>
        <div v-show="!showingSingleEvent">
            <h1
                v-if="iFrameSettings.heading"
                class="display-2">
                {{ iFrameSettings.heading }}
            </h1>
            <KeepAlive>
                <IFrameFilterSettings
                    v-if="showFilterComponent"
                    v-model:user-parameter="localUserParameter"
                    :display-filters="displayFilters"
                    :available-filters="availableFilters"
                    :i-frame-type="iFrameSettings.Type" />
            </KeepAlive>
            <div v-if="loading || events.length">
                <div class="d-flex justify-content-between align-items-center my-3">
                    <BasePagination
                        v-if="paginateEvents && paginatedDisplayType"
                        id="upper-pagination"
                        :model-value="currentPage"
                        :page-count="lastPage"
                        iframe
                        @update:model-value="$emit('loadSpecificPage', $event)" />
                    <DisplaySelection
                        v-if="allowDisplayChange"
                        class="flex-grow-1"
                        :display-type="displayType"
                        :available-display-types="availableDisplayTypes"
                        :iframe-type="iFrameSettings.Type"
                        @select="displayType = $event" />
                </div>

                <KeepAlive>
                    <component
                        :is="displayComponent"
                        :key="displayComponent"
                        :events="eventsForCurrentDisplayType"
                        :loading="loading"
                        :i-frame-type="iFrameSettings.Type"
                        :showing-single-event="showingSingleEvent"
                        :display-collapsed="displayCollapsed"
                        :color-scheme="colorScheme"
                        :show-number-of-loading-entries="loading ? perPage : 0"
                        :parent-url="parentUrl"
                        :embed-structured-data="embedStructuredData"
                        :hide-categories="hideCategories"
                        @hook:mounted="loadedDisplayComponent = true"
                        @update:show-event="$emit('showEvent', $event)" />
                </KeepAlive>
                <div class="d-flex justify-content-end align-items-center mt-5">
                    <BasePagination
                        v-if="paginateEvents && paginatedDisplayType"
                        :model-value="currentPage"
                        :page-count="lastPage"
                        iframe
                        @update:model-value="$emit('loadSpecificPage', $event)" />
                </div>
                <div
                    v-if="icalFeedActive && icalFeedUrl"
                    class="text-xs mt-4">
                    <div>
                        <span>{{ $t('iFrame.subscribeCalendar') }}</span>
                    </div>
                    <div>
                        <a
                            :href="icalFeedUrl"
                            class="text-primary pointer"
                            @click.prevent="copyToClipboard(icalFeedUrl)">
                            <font-awesome-icon
                                fixed-width
                                :icon="copiedToClipboard ? 'check' : 'copy'" />
                            {{ $t('iFrame.copyIcalUrl') }}
                        </a>
                        <span> | </span>
                        <a
                            :href="icalFeedUrl"
                            target="_blank"
                            class="text-primary pointer">
                            <font-awesome-icon
                                fixed-width
                                icon="download" />
                            {{ $t('iFrame.downloadIcalFile') }}
                        </a>
                    </div>
                </div>
            </div>
            <div v-else>
                <card>
                    <h2 class="text-center">
                        <template v-if="iFrameSettings.Type === 'events'">
                            {{ $t('iFrame.noEvents') }}
                        </template>
                        <template v-if="iFrameSettings.Type === 'appointments'">
                            {{ $t('iFrame.noAppointments') }}
                        </template>
                    </h2>
                </card>
            </div>
        </div>
    </div>
</template>

<script>
import IFrameFilterSettings from "@/components/pages/eventIFrame/IFrameFilterSettings";
import iframeFilterOptions from "@/util/consts/iframeFilterOptions";
import uniq from "lodash/uniq";
import lazyLoadedComponentsMixin from '@/mixins/lazyLoadedComponentsMixin'
import IFrameEventMapping from './IFrameEventMapping'
import IFrameEventListing from './IFrameEventListing'
import IFrameEventCalendar from './IFrameEventCalendar'
import IFrameEventTiling from './IFrameEventTiling'
import IFrameEventCompactListing from './IFrameEventCompactListing'

const IFrameSingleEvent = () => ({
    component: import(/* webpackChunkName: "single-event" */"./IFrameSingleEvent"),
    loading: DefaultSpinner
});

export default {
    name: "IFramePresentation",
    components: {
        IFrameSingleEvent,
        IFrameFilterSettings
    },
    mixins: [lazyLoadedComponentsMixin],
    provide() {
        return {
            iFrameType: () => this.iFrameSettings?.Type
        }
    },
    props: {
        events: {
            type: Array
        },
        eventOverview: {
            type: Array
        },
        iFrameSettings: {
            type: Object
        },
        availableFilters: {
            type: Object
        },
        loading: {
            type: Boolean,
            required: true
        },
        perPage: {
            type: Number,
            required: true
        },
        exceptionWhileLoadingEvents: {
            type: Boolean,
            required: true
        },
        paginateEvents: {
            type: Boolean,
            default: false
        },
        userParameter: {
            type: Object,
            required: true
        },
        messageSourceRandomId: {
            type: String
        },
        showEvent: {
            type: Object
        },
        currentPage: {
            type: [String, Number]
        },
        lastPage: {
            type: [String, Number]
        },
        parentUrl: {
            type: String
        }
    },
    data() {
        return {
            localDisplayType: null,
            loadedDisplayComponent: false,
            copiedToClipboard: false,
            root: null
        }
    },
    computed: {
        showingSingleEvent() {
            return !!(this.showEvent?.id || this.showEvent?.offerNumber);
        },
        singleEventToShow() {
            if (!this.showingSingleEvent) return null
            return this.events?.find(e => (this.showEvent.offerNumber && e.offerNumber === this.showEvent.offerNumber)
                || (this.showEvent.id && e.id == this.showEvent.id))
        },
        messageSourceString() {
            let string = "events-iframe"
            //Um die Kompatibilität zu bereits eingebundenen I-Frames zu wahren, schauen wir erst ob uns die
            //messageSourceRandomId überhaupt übergeben worden ist. Nur dann ändern wir den messageSourceString
            if (this.messageSourceRandomId) {
                string += "-" + this.messageSourceRandomId
            }
            return string
        },
        localUserParameter: {
            get() {
                return this.userParameter
            },
            set(val) {
                this.$emit('update:userParameter', val)
            }
        },
        displayComponent() {
            switch (this.displayType) {
                case 'map':
                    return IFrameEventMapping
                case 'date':
                    return IFrameEventCalendar
                case 'tile':
                    return IFrameEventTiling
                case 'compact':
                    return IFrameEventCompactListing
                default:
                    return IFrameEventListing
            }
        },
        eventsForCurrentDisplayType() {
            switch (this.displayType) {
                case 'map':
                case 'date':
                case 'compact':
                    return this.eventOverview
                default:
                    return this.events
            }
        },
        displaySingleEventButtonLabel() {
            switch (this.displayType) {
                case 'map':
                    return this.$t('iFrame.backToMap')
                case 'date':
                    return this.$t('iFrame.backToCalendar')
                case 'compact':
                    return this.$t('iFrame.backToList')
                default:
                    return null
            }
        },
        displayType: {
            get() {
                return this.localDisplayType || this.iFrameSettings?.display?.type || 'list'
            },
            set(type) {
                this.localDisplayType = type
            }
        },
        availableDisplayTypes() {
            let allAvailableDisplayTypes = []
            allAvailableDisplayTypes.push(this.iFrameSettings?.display?.type || 'list')
            if (this.iFrameSettings?.display?.allowedTypes) {
                allAvailableDisplayTypes = allAvailableDisplayTypes.concat(this.iFrameSettings.display.allowedTypes)
            }
            return uniq(allAvailableDisplayTypes)
        },
        allowDisplayChange() {
            return this.availableDisplayTypes?.length > 1
        },
        tilingAsDefault() {
            return this.iFrameSettings?.display?.tilingAsDefault
        },
        displayFilters() {
            return this.iFrameSettings?.display?.filter
        },
        useCustomTheme() {
            return !!this.iFrameSettings?.display?.customTheme
        },
        hideCategories() {
            return !!this.iFrameSettings?.display?.hideCategories
        },
        displayCollapsed() {
            return this.iFrameSettings?.display?.collapsed || 'automatic'
        },
        useFixedMargin() {
            return this.useCustomTheme && this.iFrameSettings?.display?.theme?.fixedMargin
        },
        fixedMargin() {
            return this.iFrameSettings?.display?.theme?.margin || 0
        },
        showFilterComponent() {
            let filters = this.displayFilters
            if (!filters || !filters.length) return false
            if (!this.availableFilters?.categoriesUsedInEvents?.length) filters = filters.filter(f => f !== this.iframeFilterOptions.FILTER_CATEGORIES)
            return !!filters.length
        },
        iframeFilterOptions() {
            return iframeFilterOptions
        },
        colorScheme() {
            return {
                text: this.useCustomTheme ? this.iFrameSettings?.display?.theme?.textColor || '#525f7f' : '#525f7f',
                heading: this.useCustomTheme ? this.iFrameSettings?.display?.theme?.headingColor || '#32325d' : '#32325d',
                primary: this.useCustomTheme ? this.iFrameSettings?.display?.theme?.primaryColor || '#18298C' : '#18298C'
            }
        },
        paginatedDisplayType() {
            return this.displayType === 'list' || this.displayType === 'tile'
        },
        icalFeedActive() {
            return this.iFrameSettings?.features?.icalFeed || false
        },
        icalFeedUrl() {
            return this.iFrameSettings?.features?.icalFeedUrl ?? null
        },
        embedStructuredData() {
            return this.iFrameSettings?.features?.structuredData || false
        },
        eventsValidForIndexing() {
            if (this.loading || !this.eventOverview) return []
            return this.eventOverview.filter(event => !!(event.startDate && event.endDate)
                && ((event.isOnline && event.onlineLink) || (!event.isOnline && event.location)))
        }
    },
    methods: {
        startHeightObserver() {
            if (window.ResizeObserver) {
                // create an Observer instance
                const resizeObserver = new window.ResizeObserver(entries =>
                    this.postMessageToParentWindowWithCurrentHeight(entries[0].target.clientHeight)
                )

                // start observing a DOM node
                resizeObserver.observe(document.body)
            } else {
                //In case Browser does not support ResizeObserver
                setInterval(() => {
                    this.postMessageToParentWindowWithCurrentHeight(document.body.clientHeight)
                }, 1000)
            }
        },
        postMessageToParentWindowWithCurrentHeight(height) {
            window.top.postMessage({height: height, source: this.messageSourceString}, '*')
        },
        listenToMoreEventRequestMessages() {
            window.addEventListener('message', (event) => {
                // message that was passed from iframe page
                const message = event.data;
                if (message.source === "parent-events-window"
                    && message.loadMoreEvents
                    && this.loadedDisplayComponent
                    && !this.paginateEvents
                    && this.paginatedDisplayType) {
                    this.$emit('loadNextPage')
                }
            });
        },
        postLoadedMessageToParentWindow() {
            window.top.postMessage({iFrameLoaded: true, source: this.messageSourceString}, '*')
        },
        getCustomFontSize() {
            if (!(this.useCustomTheme && !!this.iFrameSettings?.display?.theme?.adjustFontSize)) return 'inherit'
            return (16 + parseInt(this.iFrameSettings?.display?.theme?.adjustFontSize || 0) + 'px')
        },
        copyToClipboard(string) {
            navigator.clipboard.writeText(string)
            this.copiedToClipboard = true
        }
    },
    mounted() {
        // BUG FIX: Firefox has a bug where it does not render the iframe correctly if it is not visible when the page loads
        this.postMessageToParentWindowWithCurrentHeight(1)
        this.startHeightObserver()
        this.listenToMoreEventRequestMessages()
        this.postLoadedMessageToParentWindow()
        this.root = document.documentElement;
    },
    beforeUnmount() {
        window.removeEventListener("scroll", this.handleScroll)
    },
    watch: {
        iFrameSettings: {
            handler() {
                this.$nextTick(() => {
                    this.root.style.setProperty("--custom-text-color", this.colorScheme.text);
                    this.root.style.setProperty("--custom-heading-color", this.colorScheme.heading);
                    this.root.style.setProperty("--custom-primary-color", this.colorScheme.primary);
                    this.root.style.setProperty("--fc-button-bg-color", this.colorScheme.primary);
                    this.root.style.setProperty("--fc-button-hover-bg-color", this.colorScheme.primary);
                    this.root.style.setProperty("--fc-button-active-bg-color", this.colorScheme.primary);
                    this.root.style.setProperty("--fc-button-border-color", this.colorScheme.primary);
                    this.root.style.setProperty("--fc-button-hover-border-color", this.colorScheme.primary);
                    this.root.style.setProperty("--fc-button-active-border-color", this.colorScheme.primary);
                    this.root.style.setProperty("--custom-font-family", this.useCustomTheme ? this.iFrameSettings?.display?.theme?.fontFamily || 'Open Sans' : 'Open Sans');
                    this.root.style.setProperty("--custom-font-size", this.getCustomFontSize())
                })
            },
            immediate: true
        }
    }
}
</script>

<style lang="scss">
body {
    background: none !important;
    overflow: hidden;

    & .el-tree-node__content {
        height: 1.625rem !important; // 26px
    }

    & .dropdown-item,
    & .el-tree-node__label {
        font-size: .875rem !important; // 14px
    }

    & .el-tooltip__popper,
    & .el-tree-node__expand-icon {
        font-size: .75rem !important; // 12px
    }

    & .el-tree-node__expand-icon.el-tree-node__expand-icon {
        padding: .375rem !important; // 6px
    }
}

.smaller-button {
    padding: 0.5rem;
}

:root {
    font-size: var(--custom-font-size);

    .text-theme-primary {
        color: var(--custom-primary-color);
    }

    .text-theme-heading {
        color: var(--custom-heading-color);
    }

    .text-theme-text {
        color: var(--custom-text-color);
    }
}

.inject-theming {
    font-family: var(--custom-font-family), sans-serif;
    color: var(--custom-text-color);

    &.disable-container {
        max-width: none;
        width: auto;
    }

    .el-tree {
        color: var(--custom-text-color);
    }

    h1, h2, h3, h4, h5, h6, .h1, .h2, .h3, .h4, .h5, .h6 {
        color: var(--custom-heading-color);

        & > svg {
            color: var(--custom-text-color);
        }
    }

    a {
        color: var(--custom-primary-color) !important;
    }

    .event-compact-link {
        color: var(--custom-primary-color) !important;
    }

    .form-control-label {
        color: var(--custom-text-color);
    }

    .selected-badge {
        background-color: var(--custom-primary-color) !important;
    }

    .btn-primary {
        background-color: var(--custom-primary-color);
        border-color: transparent;
    }

    .bg-primary {
        background-color: var(--custom-primary-color) !important;
    }

    .text-primary {
        color: var(--custom-primary-color);
    }

    .btn-outline-primary {
        color: var(--custom-primary-color);
        border-color: var(--custom-primary-color);

        &:hover,
        &:not(:disabled):not(.disabled):active,
        &:not(:disabled):not(.disabled).active {
            background-color: var(--custom-primary-color);
            color: white;
            border-color: var(--custom-primary-color);
        }
    }

    .page-item.active > .page-link {
        background-color: var(--custom-primary-color) !important;
        border-color: var(--custom-primary-color) !important;
    }
}
</style>
